import { faKey, faLockAlt, faMobileAlt } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Flex, Label, Text, useOpenModal } from '@modusign/design-system';
import {
  useDigitalCertificationMethod,
  useMobileIdentificationMethod,
  usePasswordMethod,
} from '@modusign/react-signing';
import { useUIState } from '@modusign/react-signing/setup';
import { Signer } from '@modusign/react-signing/types';

import { formatContact } from '@app/features/apply-react-signing/libs/formatter/formatContact';
import TemplateSignerVerificationModal from '@app/features/template/components/TemplateSignerDetailSetUpCard/TemplateSignerVerificationModal';
import createOrdinalCharacter, { AllowedOrdinalNumbers } from '@app/libs/ordinalCharacter';

interface TemplateSignerDetailSetUpItemProps {
  signer: Signer;
}

export default function TemplateSignerDetailSetUpItem({
  signer,
}: TemplateSignerDetailSetUpItemProps) {
  const { ui } = useUIState();

  const { method: passwordMethod } = usePasswordMethod(signer.id);
  const { method: mobileIdentificationMethod } = useMobileIdentificationMethod(signer.id);
  const { method: digitalCertificationMethod } = useDigitalCertificationMethod(signer.id);

  const isVerificationEmpty =
    !passwordMethod && !mobileIdentificationMethod && !digitalCertificationMethod;

  const openModal = useOpenModal();

  return (
    <Box backgroundColor="white" borderRadius="3px" boxShadow="0 1px 3px 0 rgba(0,0,0,0.2)" p={6}>
      <Flex flexDirection="column" gap={4} justifyContent="center">
        <Label fontSize={2} mb={0}>
          {ui.isInOrder && createOrdinalCharacter(signer.order as AllowedOrdinalNumbers)} 서명자
          {signer?.role && (
            <Text color="textEmphasisBase" fontWeight="medium" ml={1}>
              {signer.role}
            </Text>
          )}
        </Label>
        <Box
          borderColor="boxBorderNeutralBase"
          borderStyle="solid"
          borderWidth="1px"
          p={4}
          sx={{ wordBreak: 'break-all' }}
        >
          <Flex flexDirection="column" mb="2px">
            {signer.name ? (
              <>
                {!signer.contact && (
                  <Text color="textNeutralLight" fontSize="xs">
                    이름
                  </Text>
                )}
                <Text fontWeight="medium">{signer.name}</Text>
              </>
            ) : (
              <Text color="textNeutralLight" fontSize="xs">
                설정된 이름이 없습니다.
              </Text>
            )}
          </Flex>
          <Text>{formatContact(signer.signingMethod, signer.contact)}</Text>
        </Box>
        {isVerificationEmpty ? (
          <Button
            height="36px"
            width="100%"
            onClick={() => {
              openModal((modalProps) => {
                return <TemplateSignerVerificationModal signerId={signer.id} {...modalProps} />;
              });
            }}
          >
            <Text
              as={FontAwesomeIcon}
              color="textNeutralLight"
              fontSize="sm"
              icon={faLockAlt}
              mr="3px"
            />
            인증 수단 추가
          </Button>
        ) : (
          <Flex flexDirection="column">
            <Button
              backgroundColor="boxBackgroundNeutralBase"
              border="1px solid"
              borderColor="boxBorderNeutralBase"
              fontSize="md"
              height="36px"
              textAlign="center"
              width="100%"
              onClick={() =>
                openModal((modalProps) => {
                  return <TemplateSignerVerificationModal signerId={signer.id} {...modalProps} />;
                })
              }
            >
              <Text
                as={FontAwesomeIcon}
                color="iconNeutralSlightlyDark"
                fontSize="xs"
                fontWeight="medium"
                icon={faLockAlt}
                mr="3px"
              />
              인증 수단 수정
            </Button>
            {passwordMethod && (
              <Flex
                alignItems="center"
                backgroundColor="boxBackgroundNeutralBase"
                border="1px solid"
                borderColor="boxBorderNeutralBase"
                borderTop={0}
                p={3}
              >
                <Text
                  as={FontAwesomeIcon}
                  color="iconPrimaryBase"
                  fontSize="18px"
                  icon={faLockAlt}
                  mr={2}
                />
                <Text color="textNeutralSlightlyLight">접근 암호 인증</Text>
              </Flex>
            )}
            {mobileIdentificationMethod && (
              <Flex
                alignItems="center"
                backgroundColor="boxBackgroundNeutralBase"
                border="1px solid"
                borderColor="boxBorderNeutralBase"
                borderTop={0}
                p={3}
              >
                <Text
                  as={FontAwesomeIcon}
                  color="iconPrimaryBase"
                  fontSize="18px"
                  icon={faMobileAlt}
                  mr={2}
                />
                <Text color="textNeutralSlightlyLight">휴대폰 본인 인증</Text>
              </Flex>
            )}
            {digitalCertificationMethod && (
              <Flex
                alignItems="center"
                backgroundColor="boxBackgroundNeutralBase"
                border="1px solid"
                borderColor="boxBorderNeutralBase"
                borderTop={0}
                p={3}
              >
                <Text
                  as={FontAwesomeIcon}
                  color="iconPrimaryBase"
                  fontSize="18px"
                  icon={faKey}
                  mr={2}
                />
                <Text color="textNeutralSlightlyLight">법인 공동인증서 인증</Text>
              </Flex>
            )}
          </Flex>
        )}
      </Flex>
    </Box>
  );
}
